import React, { useEffect, useRef, useCallback } from 'react';

export interface UseAutoScrollOptions {
  /**
   * Whether auto-scroll is enabled
   */
  enabled?: boolean;

  /**
   * Dependencies that trigger scroll
   */
  dependencies?: any[];

  /**
   * Whether currently streaming/loading content
   */
  isStreaming?: boolean;
}

export interface UseAutoScrollReturn {
  /**
   * Ref to attach to the scrollable container
   */
  containerRef: React.RefObject<HTMLDivElement>;

  /**
   * Ref to attach to an element at the end of scrollable content
   */
  scrollAnchorRef: React.RefObject<HTMLDivElement>;

  /**
   * Manually trigger scroll to bottom
   */
  scrollToBottom: () => void;
}

/**
 * Simple, reliable auto-scroll hook for chat interfaces
 *
 * Scrolls to bottom when:
 * - A new message arrives (dependencies change)
 * - Content is streaming (isStreaming = true)
 *
 * ChatGPT-style behavior: When user sends a message, it scrolls to the top
 * of the viewport, creating space below for the streaming response.
 *
 * Stops auto-scroll immediately when user manually scrolls
 * Resumes auto-scroll only when streaming stops (new message)
 */
export const useAutoScroll = ({
  enabled = true,
  dependencies = [],
  isStreaming = false,
}: UseAutoScrollOptions = {}): UseAutoScrollReturn => {
  const containerRef = useRef<HTMLDivElement>(null);
  const scrollAnchorRef = useRef<HTMLDivElement>(null);
  const userHasInterruptedRef = useRef(false);
  const isAutoScrollingRef = useRef(false);
  const lastDepsLengthRef = useRef(0);

  /**
   * Scroll to bottom instantly
   */
  const scrollToBottom = useCallback(() => {
    if (!enabled || !scrollAnchorRef.current) return;

    // Mark that we're auto-scrolling
    isAutoScrollingRef.current = true;

    // Scroll to bottom
    scrollAnchorRef.current.scrollIntoView({
      behavior: 'instant',
      block: 'end',
    });

    // Clear the flag after a short delay
    setTimeout(() => {
      isAutoScrollingRef.current = false;
    }, 100);
  }, [enabled]);

  /**
   * ChatGPT-style scroll: Scroll latest message into view
   * Now using normal flex direction, just scroll to bottom
   */
  const scrollMessageToTop = useCallback(() => {
    // Just use the same logic as scrollToBottom - scroll to the anchor
    scrollToBottom();
  }, [scrollToBottom]);

  /**
   * Handle scroll events to detect user intervention
   */
  useEffect(() => {
    const container = containerRef.current;
    if (!enabled || !container) return undefined;

    const handleScroll = () => {
      // Ignore scroll events we triggered
      if (isAutoScrollingRef.current) return;

      // If we're streaming and user scrolls, stop auto-scrolling immediately
      if (isStreaming) {
        userHasInterruptedRef.current = true;
      }
    };

    container.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      container.removeEventListener('scroll', handleScroll);
    };
  }, [enabled, isStreaming]);

  /**
   * Reset interrupt flag when streaming stops (new message arrives)
   */
  useEffect(() => {
    if (!isStreaming) {
      userHasInterruptedRef.current = false;
    }
  }, [isStreaming]);

  /**
   * Auto-scroll when dependencies change (new messages arrive)
   */
  useEffect(() => {
    if (!enabled) return;

    const currentDepsLength = dependencies[0] || 0; // First dependency is conversation length
    const hasNewMessage = currentDepsLength > lastDepsLengthRef.current;
    lastDepsLengthRef.current = currentDepsLength;

    // When not streaming, check if we have a new message
    if (!isStreaming) {
      if (hasNewMessage) {
        // New message submitted - scroll to top ChatGPT-style
        requestAnimationFrame(() => {
          scrollMessageToTop();
        });
      }
      return;
    }

    // When streaming starts fresh (no user interruption yet), auto-scroll
    if (!userHasInterruptedRef.current) {
      requestAnimationFrame(() => {
        scrollToBottom();
      });
    }
  }, [
    enabled,
    scrollToBottom,
    scrollMessageToTop,
    isStreaming,
    ...dependencies,
  ]);

  /**
   * Auto-scroll during streaming - runs frequently during streaming
   * Stops immediately if user has scrolled
   */
  useEffect(() => {
    if (!enabled || !isStreaming || userHasInterruptedRef.current)
      return undefined;

    const scrollInterval = setInterval(() => {
      // Double-check user hasn't interrupted
      if (!userHasInterruptedRef.current && scrollAnchorRef.current) {
        scrollAnchorRef.current.scrollIntoView({
          behavior: 'instant',
          block: 'end',
        });
      }
    }, 50); // Scroll every 50ms during streaming for smooth updates

    return () => clearInterval(scrollInterval);
  }, [enabled, isStreaming]);

  return {
    containerRef,
    scrollAnchorRef,
    scrollToBottom,
  };
};
